/* hs-eslint ignored failing-rules */
/* eslint-disable conversations/no-dynamic-i18n */

import { Fragment, memo } from 'react';
import { ContactsIcon } from 'foundations-assets';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedName from 'I18n/components/FormattedName';
import FormattedPhoneNumber from 'I18n/components/FormattedPhoneNumber';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { getDynamicNamespace } from 'conversations-infrastructure/i18n/getDynamicNamespace';
import styled from 'foundations-theming/utils';
import { Text } from 'foundations-components/components/Text';
import { getContactProfile, getFirstName, getMiddleName, getLastName, getCompany, getPhoneNumbers, getPhoneNumber, getEmails, getEmail, getUrls, getUrl, getAddresses, getStreet, getCity, getState, getZip, getCountry, getType } from 'conversations-message-history/attachments/operators/contactAttachmentGetters';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ContactMetadataWrapper = styled.div.withConfig({
  displayName: "ContactAttachment__ContactMetadataWrapper",
  componentId: "sc-1917329-0"
})(({
  theme
}) => {
  return {
    'margin-left': `${theme.space['600']}`
  };
});
const MetadataHeader = styled.div.withConfig({
  displayName: "ContactAttachment__MetadataHeader",
  componentId: "sc-1917329-1"
})(({
  theme
}) => {
  return {
    'margin-top': `${theme.space['250']}`,
    'font-size': `${theme.typography['detail-100'].fontSize}`,
    color: `${theme.color['border-core-hover']}`
  };
});
const getHeader = (metadataType, valueType) => getDynamicNamespace({
  prefix: `conversations-inbox-message-history.common-message.attachmentRendering.sharedContact.${metadataType}`,
  translationOptions: ['HOME', 'WORK', 'CELL', 'MAIN', 'default'],
  defaultOption: 'default'
}, valueType || 'default');
const renderPhoneNumbers = phoneNumbers => {
  if (!phoneNumbers || !phoneNumbers.length) return null;
  return phoneNumbers.map((entry, index) => {
    const type = getType(entry);
    const number = getPhoneNumber(entry);
    return /*#__PURE__*/_jsxs("div", {
      "data-test-id": type ? `contact-phone-${type}` : 'contact-phone',
      children: [/*#__PURE__*/_jsx(MetadataHeader, {
        "data-test-id": "contact-phone-header",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: getHeader('phone', type),
          options: {
            type: type && type.toLowerCase()
          }
        })
      }), /*#__PURE__*/_jsx("div", {
        "data-test-id": "contact-phone-value",
        children: /*#__PURE__*/_jsx(FormattedPhoneNumber, {
          value: number
        })
      })]
    }, `contact-phone-${index}`);
  });
};
const renderEmails = emails => {
  if (!emails || !emails.length) return null;
  return emails.map((entry, index) => {
    const type = getType(entry);
    const email = getEmail(entry);
    return /*#__PURE__*/_jsxs("div", {
      "data-test-id": type ? `contact-email-${type}` : 'contact-email',
      children: [/*#__PURE__*/_jsx(MetadataHeader, {
        "data-test-id": "contact-email-header",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: getHeader('email', type),
          options: {
            type: type && type.toLowerCase()
          }
        })
      }), /*#__PURE__*/_jsx("div", {
        "data-test-id": "contact-email-value",
        children: email
      })]
    }, `contact-email-${index}`);
  });
};
const renderUrls = urls => {
  if (!urls || !urls.length) return null;
  return urls.map((entry, index) => {
    const type = getType(entry);
    const url = getUrl(entry);
    return /*#__PURE__*/_jsxs("div", {
      "data-test-id": type ? `contact-url-${type}` : 'contact-url',
      children: [/*#__PURE__*/_jsx(MetadataHeader, {
        "data-test-id": "contact-url-header",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: getHeader('url', type),
          options: {
            type: type && type.toLowerCase()
          }
        })
      }), /*#__PURE__*/_jsx("div", {
        "data-test-id": "contact-url-value",
        children: url
      })]
    }, `contact-url-${index}`);
  });
};
const renderAddresses = addresses => {
  if (!addresses || !addresses.length) return null;
  return addresses.map((entry, index) => {
    const type = getType(entry);
    const street = getStreet(entry);
    const city = getCity(entry);
    const state = getState(entry);
    const zip = getZip(entry);
    const country = getCountry(entry);
    return /*#__PURE__*/_jsxs("div", {
      "data-test-id": type ? `contact-address-${type}` : 'contact-address',
      children: [/*#__PURE__*/_jsx(MetadataHeader, {
        "data-test-id": "contact-address-header",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: getHeader('address', type),
          options: {
            type: type && type.toLowerCase()
          }
        })
      }), /*#__PURE__*/_jsxs("div", {
        "data-test-id": "contact-address-value",
        children: [/*#__PURE__*/_jsx("div", {
          children: street
        }), /*#__PURE__*/_jsxs("div", {
          children: [city, state && `, ${state}`, zip && ` ${zip}`]
        }), /*#__PURE__*/_jsx("div", {
          children: country
        })]
      })]
    }, `contact-address-${index}`);
  });
};
const ContactAttachment = ({
  contactMetadata
}) => {
  const profile = getContactProfile(contactMetadata);
  const givenName = getFirstName(profile);
  const middleName = getMiddleName(profile);
  const familyName = getLastName(profile);
  const company = getCompany(profile);
  const phoneNumbers = getPhoneNumbers(profile);
  const emails = getEmails(profile);
  const urls = getUrls(profile);
  const addresses = getAddresses(profile);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(Text, {
      marginBlockEnd: "100",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "conversations-inbox-message-history.common-message.attachmentRendering.sharedContact.header"
      })
    }), /*#__PURE__*/_jsx(TFlex, {
      align: "start",
      justify: "start",
      direction: "row",
      wrap: "nowrap",
      children: /*#__PURE__*/_jsxs("div", {
        "data-test-id": "metadata",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          children: [/*#__PURE__*/_jsx(View, {
            as: "span",
            marginInlineEnd: "100",
            marginBlockStart: "100",
            children: /*#__PURE__*/_jsx(ContactsIcon, {
              size: "400"
            })
          }), /*#__PURE__*/_jsx(Text, {
            typography: "heading-50",
            "data-test-id": "contact-name",
            children: givenName || familyName ? /*#__PURE__*/_jsx(FormattedName, {
              "data-test-id": "formatted-contact-name",
              givenName: middleName ? `${givenName} ${middleName}` : givenName,
              familyName: familyName
            }) : /*#__PURE__*/_jsx(Text, {
              color: 'border-core-hover',
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "conversations-inbox-message-history.common-message.attachmentRendering.sharedContact.noName"
              })
            })
          })]
        }), /*#__PURE__*/_jsxs(ContactMetadataWrapper, {
          children: [company && /*#__PURE__*/_jsxs("div", {
            "data-test-id": "contact-company",
            children: [/*#__PURE__*/_jsx(MetadataHeader, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "conversations-inbox-message-history.common-message.attachmentRendering.sharedContact.company"
              })
            }), /*#__PURE__*/_jsx("div", {
              "data-test-id": "contact-company-value",
              children: company
            })]
          }), phoneNumbers && renderPhoneNumbers(phoneNumbers), emails && renderEmails(emails), urls && renderUrls(urls), addresses && renderAddresses(addresses)]
        })]
      })
    })]
  });
};
ContactAttachment.displayName = 'ContactAttachment';
export default /*#__PURE__*/memo(ContactAttachment);